/**
 * AzurePipelinesSetupWizard Component
 *
 * Setup wizard for adding LeftShield task to Azure Pipelines.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Text,
  makeStyles,
  tokens,
  Divider,
  MessageBar,
  MessageBarBody,
  Link,
  RadioGroup,
  Radio,
} from '@fluentui/react-components';
import {
  CopyRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    minWidth: '650px',
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  stepNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  codeBlock: {
    position: 'relative',
    fontFamily: 'monospace',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
  },
  copyButton: {
    position: 'absolute',
    top: tokens.spacingVerticalXS,
    right: tokens.spacingHorizontalXS,
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

interface AzurePipelinesSetupWizardProps {
  open: boolean;
  onClose: () => void;
  apiKey?: string;
}

export const AzurePipelinesSetupWizard: React.FC<AzurePipelinesSetupWizardProps> = ({
  open,
  onClose,
  apiKey = 'YOUR_API_KEY',
}) => {
  const styles = useStyles();
  const [copiedStep, setCopiedStep] = useState<number | null>(null);
  const [failOn, setFailOn] = useState<'block' | 'warn'>('block');

  const copyToClipboard = (text: string, step: number) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(step);
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const pipelineYaml = `trigger:
  branches:
    include:
      - main
      - develop

pr:
  branches:
    include:
      - main

stages:
  - stage: SecurityScan
    displayName: 'Security Scan'
    jobs:
      - job: LeftShieldValidation
        displayName: 'LeftShield Validation'
        pool:
          vmImage: 'ubuntu-latest'
        steps:
          - task: LeftShieldValidate@1
            displayName: 'Validate Code'
            inputs:
              apiKey: $(LEFTSHIELD_API_KEY)
              failOn: '${failOn}'
              scanTypes: 'sast,secrets,sca'
              publishResults: true`;

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Set Up Azure Pipelines</DialogTitle>
          <DialogContent className={styles.content}>
            <MessageBar intent="info">
              <MessageBarBody>
                The LeftShield Azure Pipelines task integrates security scanning into your CI/CD pipeline with build summary reporting.
              </MessageBarBody>
            </MessageBar>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>1</span>
                <Text weight="semibold">Install Extension from Marketplace</Text>
              </div>
              <Text size={200}>
                Install the LeftShield extension from the{' '}
                <Link href="https://marketplace.visualstudio.com/items?itemName=leftshield.leftshield-validate" target="_blank">
                  Visual Studio Marketplace
                </Link>
              </Text>
            </div>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>2</span>
                <Text weight="semibold">Add API Key as Pipeline Variable</Text>
              </div>
              <Text size={200}>
                Go to Pipelines → Library → Variable groups, or add directly to your pipeline:
              </Text>
              <div className={styles.codeBlock}>
                <Text size={200}>
                  <strong>Name:</strong> LEFTSHIELD_API_KEY{'\n'}
                  <strong>Value:</strong> {apiKey}{'\n'}
                  <strong>Keep this value secret:</strong> Yes
                </Text>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 2 ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(apiKey, 2)}
                />
              </div>
            </div>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>3</span>
                <Text weight="semibold">Configure Failure Behavior</Text>
              </div>
              <RadioGroup
                value={failOn}
                onChange={(_, data) => setFailOn(data.value as 'block' | 'warn')}
              >
                <div className={styles.option}>
                  <Radio value="block" label="Fail on blocking violations (recommended)" />
                  <Text size={200} style={{ marginLeft: '28px', color: tokens.colorNeutralForeground3 }}>
                    Builds will fail if critical/high severity issues are found
                  </Text>
                </div>
                <div className={styles.option}>
                  <Radio value="warn" label="Fail on any violations" />
                  <Text size={200} style={{ marginLeft: '28px', color: tokens.colorNeutralForeground3 }}>
                    Builds will fail for any enforcement decision
                  </Text>
                </div>
              </RadioGroup>
            </div>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>4</span>
                <Text weight="semibold">Add Task to Pipeline</Text>
              </div>
              <Text size={200}>
                Add the following to your <code>azure-pipelines.yml</code>:
              </Text>
              <div className={styles.codeBlock}>
                <pre style={{ margin: 0 }}>{pipelineYaml}</pre>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 4 ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(pipelineYaml, 4)}
                />
              </div>
            </div>

            <Divider />

            <MessageBar intent="success">
              <MessageBarBody>
                Results will appear in the build summary tab.{' '}
                <Link href="https://docs.leftshield.io/azure-pipelines" target="_blank">
                  View documentation
                </Link>
              </MessageBarBody>
            </MessageBar>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
